import { useCallback, useMemo, useState } from 'react';
import { DASHBOARDS, SpaceCockpitPositions } from './DashboardConstants';

const safeStorageGet = (key) => {
    try {
        return globalThis.localStorage?.getItem?.(key) ?? null;
    } catch {
        return null;
    }
};

const safeStorageSet = (key, value) => {
    try {
        globalThis.localStorage?.setItem?.(key, value);
    } catch {
        // ignore (e.g., quota exceeded, privacy mode, or stubbed storage in tests)
    }
};

export const validSCPositions = new Set(["left", "right", "top", "bottom"]);
export const useSCPositionSettings = () => {
    const [scPositions, setSCPositions] = useState(() => {
        let stored = safeStorageGet("SCPositions");
        if(stored) {
            try {
                stored = JSON.parse(stored);
            } catch (e) {
                console.error('Error parsing stored SpaceCockpit positions:', e);
                stored = null;
            }
        }
        const initial = {};
        for (const key of Object.keys(SpaceCockpitPositions)) {
            initial[key] = {...SpaceCockpitPositions[key]};
            if(validSCPositions.has(stored?.[key]?.position)) {
                initial[key].position = stored[key].position;
            }
        }
        return initial;
    });

    const getSCPosition = useCallback((dashboardName) => {
        const value = scPositions[dashboardName];
        return value ? { ...value } : null;
    }, [scPositions]);

    const setSCPosition = useCallback((dashboardName, position) => {
        if(dashboardName in scPositions && validSCPositions.has(position)) {
            scPositions[dashboardName].position = position;
            setSCPositions({...scPositions});

            safeStorageSet("SCPositions", JSON.stringify(scPositions));
        }
    }, [scPositions, setSCPositions]);

    return {
        getSCPosition,
        setSCPosition
    };
};

export const useLocalSettings = (sourceFilters) => {

    const [selectedSourceFilterRaw, setSelectedSourceFilterRaw] = useState(() => safeStorageGet("SelectedSourceFilter"));
    const [lastDashboardRaw, setLastDashboardRaw] = useState(() => safeStorageGet("LastDashboard"));

    const selectedSourceFilter = useMemo(() => {
        const sourceFilter = sourceFilters?.[selectedSourceFilterRaw] ?? null;
        return sourceFilter ? {...sourceFilter, name: selectedSourceFilterRaw} : null;
    }, [selectedSourceFilterRaw, sourceFilters]);

    const setSelectedSourceFilter = (name) => {
        safeStorageSet("SelectedSourceFilter", name);
        setSelectedSourceFilterRaw(name);
    };

    const lastDashboard = useMemo(() => {
        if(DASHBOARDS.includes(lastDashboardRaw))
            return lastDashboardRaw;
        return null;
    }, [lastDashboardRaw]);

    const setLastDashboard = (name) => {
        safeStorageSet("LastDashboard", name);
        setLastDashboardRaw(name);
    };

    return {
        selectedSourceFilter, setSelectedSourceFilter,
        lastDashboard, setLastDashboard
    };

};